import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";

export function resetLabel(iso) {
  if (!iso) return "5:00 AM IST tomorrow";
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "numeric", month: "short" });
    return `5:00 AM IST · ${date}`;
  } catch {
    return "5:00 AM IST tomorrow";
  }
}

export function resultLabel(iso) {
  if (!iso) return "the daily result time";
  return new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true }) + " IST";
}

const refreshedResults = new Set();
const seenResults = new Set();
function claimResult(id) {
  const key = `procrypto:trade-result:${id}`;
  if (seenResults.has(id)) return false;
  try { if (localStorage.getItem(key)) return false; localStorage.setItem(key, "1"); } catch {}
  seenResults.add(id);
  return true;
}

export function useDailyTrade(refreshUser, { resultToast = true } = {}) {
  const [trade, setTrade] = useState(null);
  const [unlocked, setUnlocked] = useState(false);
  const [nextReset, setNextReset] = useState(null);
  const [placing, setPlacing] = useState("");
  const [loading, setLoading] = useState(true);
  const [schedule, setSchedule] = useState(null);
  const requestRef = useRef(false);
  const refreshRef = useRef(refreshUser);
  refreshRef.current = refreshUser;

  const load = useCallback(async () => {
    if (requestRef.current) return;
    requestRef.current = true;
    try {
      const { data } = await api.get("/trade/today");
      setTrade(data.trade);
      setUnlocked(data.trading_unlocked);
      setNextReset(data.next_reset);
      setSchedule(data.schedule);
      if (data.trade && ["won", "lost"].includes(data.trade.status)) {
        const id = data.trade.id;
        if (!refreshedResults.has(id)) {
          refreshedResults.add(id);
          await refreshRef.current?.();
        }
        if (resultToast && claimResult(id)) {
          const payout = data.trade.payout;
          const amount = payout == null ? "" : ` ${payout > 0 ? "+" : ""}${payout} USDT.`;
          const message = `You ${data.trade.status === "won" ? "won" : "lost"}.${amount} Next bid opens ${resetLabel(data.next_reset)}`;
          toast[data.trade.status === "won" ? "success" : "error"](message, { id: `trade-result-${id}` });
        }
      }
    } catch {
    } finally {
      requestRef.current = false;
      setLoading(false);
    }
  }, [resultToast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // Refresh across daily resets too; a slow request cannot overlap itself.
    const id = setInterval(load, trade?.status === "pending" ? 10000 : 30000);
    return () => clearInterval(id);
  }, [trade?.status, load]);

  const place = useCallback(
    async (symbol, direction, accepted_rules_version) => {
      setPlacing(symbol + direction);
      try {
        const { data } = await api.post("/trade", { symbol, direction, accepted_rules_version });
        setTrade(data.trade);
        setNextReset(data.next_reset);
        toast.success(
          `Submitted ${direction === "pull" ? "PULL ▲" : "CALL ▼"} on ${symbol}. Results at ${resultLabel(data.trade.resolve_at)}.`
        );
        return true;
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
        load();
        return false;
      } finally {
        setPlacing("");
      }
    },
    [load]
  );

  return { trade, unlocked, nextReset, placing, loading, place, schedule, reload: load };
}
